import React, { useEffect, useRef, useState } from "react";
import Arrow from "@assets/images/icons/arrow-left.svg";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import Input from "@components/auth/forms/shared/Input";
import FormButton from "@components/auth/forms/shared/FormButton";
import PasswordRequirements, {
  getPasswordRequirements,
} from "@components/pages/Auth/components/PasswordRequirements";

import axios from "@helpers/axios/public.axios";
import { createPassword } from "@actions/auth";
import { useTranslation } from "@helpers/translate";
import { UPDATED_PASSWORD } from "../../../constants";
import "./CreatePassword.sass";

export const CreatePassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [myCode, setMyCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingCode, setCheckingCode] = useState(true);
  const [isCodeChecked, setIsCodeChecked] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [submitError, setSubmitError] = useState("");

  const checkedCodeRef = useRef("");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { code, email } = useParams();
  const { __ } = useTranslation();

  const passwordRequirements = getPasswordRequirements(password);

  const isPasswordRequirementsValid =
    passwordRequirements.hasMinLength &&
    passwordRequirements.hasNumbersAndLetters;

  const passwordsMatch =
    Boolean(password) &&
    Boolean(confirmPassword) &&
    password === confirmPassword;

  const shouldShowPasswordMatchError =
    Boolean(confirmPassword) && password !== confirmPassword;

  const isSubmitDisabled =
    loading || !isCodeChecked || !isPasswordRequirementsValid || !passwordsMatch;

  const isInvalidLink = !checkingCode && Boolean(codeError) && !isCodeChecked;

  useEffect(() => {
    const decodedEmail = email ? decodeURIComponent(email) : "";
    const checkKey = `${decodedEmail}:${code || ""}`;

    if (checkedCodeRef.current === checkKey) {
      return;
    }

    checkedCodeRef.current = checkKey;

    console.log("CreatePassword params:", {
      email: decodedEmail,
      code,
    });

    if (!decodedEmail || !code) {
      setCheckingCode(false);
      setIsCodeChecked(false);
      setCodeError("invalid_reset_link");
      return;
    }

    setCheckingCode(true);
    setCodeError("");
    setSubmitError("");
    setIsCodeChecked(false);

    axios
      .post("/check-code", {
        email: decodedEmail,
        code,
      })
      .then(() => {
        console.log("check-code success");

        setMyCode(code);
        setIsCodeChecked(true);
        setCodeError("");
      })
      .catch((error) => {
        console.log("check-code error:", {
          status: error?.response?.status,
          data: error?.response?.data,
        });

        setIsCodeChecked(false);
        setCodeError("invalid_reset_link");
      })
      .finally(() => {
        setCheckingCode(false);
      });
  }, [email, code]);

  const onSubmit = (event) => {
    event.preventDefault();

    if (!isPasswordRequirementsValid || !passwordsMatch) {
      return;
    }

    setLoading(true);
    setSubmitError("");

    dispatch(
      createPassword({
        myCode,
        new_password: password,
      }),
    )
      .then(() => {
        navigate(UPDATED_PASSWORD, { replace: true });
      })
      .catch((error) => {
        console.log("change-password error:", {
          status: error?.response?.status,
          data: error?.response?.data,
        });

        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Password was not changed";

        const normalizedMessage = String(message).toLowerCase();

        if (
          normalizedMessage.includes("expired") ||
          normalizedMessage.includes("already used")
        ) {
          setIsCodeChecked(false);
          setCodeError("invalid_reset_link");
          return;
        }

        setSubmitError(message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderBackLink = () => (
    <Link to="/" className="auth-form__back-link create-password__back">
      <img src={Arrow} alt="back" />
      {__("common.back_to_login")}
    </Link>
  );

  if (checkingCode) {
    return (
      <div className="create-password-page">
        <div className="auth-form create-password">
          <div className="auth-form__form create-password__form">
            {renderBackLink()}

            <h2 className="auth-form__title create-password__title">
              {__("auth.create_password")}
            </h2>

            <p className="auth-form__description create-password__description">
              {__("auth.checking_reset_link")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isInvalidLink) {
    return (
      <div className="create-password-page">
        <div className="auth-form create-password">
          <div className="auth-form__form create-password__form">
            <h2 className="auth-form__title create-password__title">
              {__("auth.reset_link_expired_title")}
            </h2>

            <p className="auth-form__description create-password__description">
              {__("auth.reset_link_expired_text")}
            </p>

            <FormButton
              variant="secondary"
              type="button"
              className="auth-form__button-wrapper create-password__button"
              onClick={() => navigate("/")}
            >
              {__("common.back_to_login")}
            </FormButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-password-page">
      <div className="auth-form create-password">
        <form
          onSubmit={onSubmit}
          className="auth-form__form create-password__form"
        >
          {renderBackLink()}

          <h2 className="auth-form__title create-password__title">
            {__("auth.create_password")}
          </h2>

          <p className="auth-form__description create-password__description">
            {__("auth.create_password_text")}
          </p>

          <Input
            label={__("auth.new_password")}
            type="password"
            message=""
            status="default"
            className="auth-form__input-wrapper create-password__input"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              setSubmitError("");
            }}
          />

          <Input
            label={__("auth.confirm_password")}
            type="password"
            message={shouldShowPasswordMatchError ? __("error.pass_different") : ""}
            messageStatus="error"
            status={shouldShowPasswordMatchError ? "error" : "default"}
            className="auth-form__input-wrapper create-password__input create-password__input--last"
            value={confirmPassword}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
              setSubmitError("");
            }}
          />

          {submitError && (
            <p className="create-password__error create-password__error--submit">
              {submitError}
            </p>
          )}

          <div className="create-password__requirements">
            <PasswordRequirements password={password} />
          </div>

          <FormButton
            variant="secondary"
            type="submit"
            className="auth-form__button-wrapper create-password__button"
            disabled={isSubmitDisabled}
          >
            {loading
              ? __("auth.loading") || "Loading..."
              : __("auth.update_password")}
          </FormButton>
        </form>
      </div>
    </div>
  );
};

export default CreatePassword;
